import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import { CTAContactForm } from "@/components/CTAContactForm";
import { InlineCTA } from "@/components/InlineCTA";
import { MapPin } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroImage from "@/assets/masonry-contractor-clifton-nj-hero.webp";
import patioImage from "@/assets/mp-patio-paver-installation-clifton-nj-04052026.webp";
import retainingWallImage from "@/assets/mp-masonry-paver-patio-clifton-nj-g-04052026.webp";
import stepsImage from "@/assets/mp-steps-and-stoops-installation-passaic-nj-041026.webp";
import concreteDrivewayImage from "@/assets/mp-concrete-driveway-clifton-nj-041026.webp";

const faqs = [
  {
    question: "Do I need a permit for a paver patio in Clifton?",
    answer: "A paver patio on your own property typically does not require a building permit in Clifton, but it does count toward your lot's impervious coverage under the city's zoning code. If your project involves changes to drainage or grading near the property line, the Building Department may need to review it. We can check what applies to your specific lot when we come out for the estimate.",
  },
  {
    question: "Do retaining walls in Clifton require engineering?",
    answer: "Retaining walls over 4 feet tall require a NJ construction permit and engineering calculations from a licensed professional engineer, per the NJ Uniform Construction Code. Walls under 4 feet still need a zoning permit in Clifton with a certified plot plan showing the wall location and elevation. We coordinate the permit process with the city so you are not dealing with that paperwork yourself.",
    link: { text: "zoning permit in Clifton with a certified", url: "https://ecode360.com/8522866" },
  },
  {
    question: "What type of soil do most Clifton properties have?",
    answer: "Most of Clifton sits on Boonton series glacial till, which is a clay-heavy soil formed from shale, basalt, and diabase. It holds water and expands when it freezes, and that movement is what causes settling, shifting, and cracking in patios, driveways, and walls over time. We account for it by excavating deeper, compacting the base in lifts, and building in drainage so the water has somewhere to go instead of sitting under your project.",
    link: { text: "Boonton series glacial till, which is a clay-heavy soil formed from shale, basalt, and diabase", url: "https://www.shorellc.com/articles/nj-soils-and-testing-guide" },
  },
  {
    question: "How long does it take to replace front steps on a Clifton home?",
    answer: "Most step and stoop replacements in Clifton take about 3 to 5 working days, depending on the size of the stoop and whether the existing footings need to come out. Weather and permit timing can shift things, but we give you a clear schedule before we start and we keep you updated if anything changes.",
  },
  {
    question: "When is the best time of year to start a patio project in Clifton?",
    answer: "Spring through early fall, roughly April to November, is the best window because the ground needs to be workable and temperatures need to stay above freezing for materials to set properly. We start booking spring projects in late winter, so if you reach out in February or March you have the best shot at getting on the schedule early in the season before things fill up.",
  },
  {
    question: "Does Masonry Pro serve all neighborhoods in Clifton?",
    answer: "Yes, we work across all of Clifton, from Montclair Heights and Allwood on the western side to Athenia, Richfield, Delawanna, and Lakeview in the central and eastern sections. Our shop in Totowa is about 10 minutes from most parts of the city, and we are in Clifton regularly for both new projects and repairs.",
  },
];

export default function Clifton() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Masonry Contractor Clifton NJ - Passaic Co. | Masonry Pro</title>
        <meta property="og:description" content="Need a masonry contractor in Clifton? Paver patios, retaining walls, concrete, steps, and driveways from a local crew in Totowa. Free estimates, no trip fees." />
        <meta
          name="description"
          content="Need a masonry contractor in Clifton? Paver patios, retaining walls, concrete, steps, and driveways from a local crew in Totowa. Free estimates, no trip fees."
        />
        <meta name="author" content="Masonry Pro Construction" />
        <meta
          name="keywords"
          content="masonry contractor, paver patio, retaining wall, stone work, North Jersey, Bergen County, Essex County, Passaic County"
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Masonry Contractor Clifton NJ - Passaic Co. | Masonry Pro" />
        <meta
          property="og:description"
          content="Need a masonry contractor in Clifton? Paver patios, retaining walls, concrete, steps, and driveways from a local crew in Totowa. Free estimates, no trip fees."
        />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://masonry-pro.com/masonry-pro-logo-2.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@MasonryPro" />
        <meta name="twitter:image" content="https://masonry-pro.com/masonry-pro-logo-2.webp" />
        <meta name="twitter:title" content="Masonry Contractor Clifton NJ - Passaic Co. | Masonry Pro" />
        <meta
          name="twitter:description"
          content="Need a masonry contractor in Clifton? Paver patios, retaining walls, concrete, steps, and driveways from a local crew in Totowa. Free estimates, no trip fees."
        />
        <link rel="preload" as="image" href={heroImage} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero Section */}
          <section className="relative">
            <img
              src={heroImage}
              alt="Clifton, NJ street sign — masonry contractor serving Clifton, Passaic County, New Jersey"
              width={1920}
              height={600}
              fetchPriority="high"
              decoding="async"
              className="w-full h-[400px] md:h-[500px] lg:h-[600px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 to-black/65 flex items-center justify-center">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto text-center">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <MapPin className="h-6 w-6" style={{ color: '#d4a853' }} />
                    <span style={{ color: '#d4a853', fontWeight: 500 }}>Passaic County, NJ</span>
                  </div>
                  <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                    Masonry Contractor in{" "}
                    <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Clifton, NJ</span>
                  </h1>
                  <p style={{ color: '#c4a24e', WebkitTextFillColor: '#c4a24e', fontSize: '0.95rem', fontWeight: 500, letterSpacing: '0.025em', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                    <Link to="/paving-services/paver-patios" style={{ color: '#c4a24e', textDecoration: 'none' }} className="hover:underline">Paver Patios</Link> | <Link to="/masonry-services/retaining-walls" style={{ color: '#c4a24e', textDecoration: 'none' }} className="hover:underline">Retaining Walls</Link> | <Link to="/masonry-services/steps-stoops" style={{ color: '#c4a24e', textDecoration: 'none' }} className="hover:underline">Steps &amp; Stoops</Link> | <Link to="/concrete-services/driveways" style={{ color: '#c4a24e', textDecoration: 'none' }} className="hover:underline">Concrete Driveways</Link> | <Link to="/masonry-services/stone-veneer" style={{ color: '#c4a24e', textDecoration: 'none' }} className="hover:underline">Stone Veneer</Link> | Residential &amp; Commercial
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Introduction Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto space-y-6 text-lg text-muted-foreground">
                <p>
                  Let me tell you a little bit about Clifton. It sits right in the middle of Passaic County and covers over 11 square miles. It has around 18 neighborhoods, and it's one of the larger towns in North New Jersey. What's interesting is that it used to be even larger. Though Clifton wasn't incorporated as a city until 1917, there was a community in the{" "}
                  <a href="https://www.cliftonnj.org/256/Hamilton-House-Museum" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">area since 1679</a>
                  <sup className="text-secondary">1</sup>. The area was called Acquackanonk Township. Back then, it included parts of what are now Little Falls, Passaic, Paterson, and West Paterson.
                </p>
                <p>
                  Masonry Pro Construction has been serving the Clifton area for over 35 years as a licensed masonry contractor. We install and repair paver patios, retaining walls, masonry steps and stoops, concrete driveways and sidewalks, and a full range of related work across the city.
                </p>
                <p>
                  Our shop in Totowa is about 10 minutes away, so our crew is in and out of Clifton on a regular basis. We serve homeowners throughout{" "}
                  <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic County</Link>, and Clifton is one of the towns we know best.
                </p>
                <p>
                  What makes Clifton different from a lot of surrounding towns is the mix of home styles and terrain you get within a single city. The western side near Garrett Mountain has some of the steepest residential lots in the county, while the eastern neighborhoods closer to Nutley and Bloomfield are flatter and more densely built. A lot of the houses were built from the 1940s through the 1970s. The home styles that were popular then were Cape Cods, split-levels, ranches, and Colonials. That means the original masonry work (front steps, driveways, foundation walls) may have 50 to 80 years of wear.
                </p>
                <p>
                  The soil across most of Clifton is Boonton series{" "}
                  <a href="https://websoilsurvey.nrcs.usda.gov/" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">glacial till</a>, which is a clay-heavy mix that holds water and expands when it freezes.<sup className="text-secondary">2</sup> That matters because the freeze-thaw cycle in North Jersey is what causes pavers to shift, concrete to crack, and retaining walls to lean over time. How we prepare the base and handle drainage on a Clifton job is directly tied to the soil condition, and we factor it into every project we build.
                </p>
                <p>
                  If you have a project in mind or something that needs fixing, we are usually able to come out for a free estimate within a few days. Call or text and we will set up a time.
                </p>
              </div>
            </div>
          </section>

          {/* Directions Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  How We Get to Clifton from <span className="text-gradient-gold">Totowa</span>
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    We're located at 36 Highview Ave in Totowa, and getting to most Clifton neighborhoods takes about 10 minutes, depending on which part of the city the job is in.
                  </p>
                  <p>
                    Our usual route is Totowa Road to Union Boulevard, then onto Route 46 East. Route 46 runs right through Clifton and connects to pretty much everything else from there. For jobs in the western neighborhoods like Albion or Montclair Heights, we take the Valley Road exit off of Route 46. For central Clifton (Athenia, Richfield, Main Mall area), we stay on 46 to the Route 19 interchange and come down Broad Street. And if the job is on the eastern side near Delawanna, Botany Village, or Lakeview, the Garden State Parkway gets us there faster than surface streets.
                  </p>
                  <p>
                    On the way in, we pass through Little Falls and cross the Passaic River, which is a route we also take to get to jobs in Woodland Park and Paterson. A lot of the towns we serve (Wayne, Passaic, Nutley, Bloomfield) are all within a few minutes of that same Route 46 corridor, so we are in this area constantly.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Paver Patios — Content LEFT, Image RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  Paver Patios Built for Clifton's <span className="text-gradient-gold">Clay Soil and Cold Winters</span>
                </h2>
                <div className="grid lg:grid-cols-5 gap-12 items-center">
                  <div className="lg:col-span-3 space-y-6 text-lg text-muted-foreground">
                    <p>
                      Paver patios are among the most common projects we do in Clifton because so many of these older homes were either built without a finished patio or the original poured concrete slab has cracked and settled over decades of use. A lot of the Cape Cods and ranches in neighborhoods like Athenia and Richfield have backyards that were never really set up for outdoor living, and homeowners want to change that.
                    </p>
                    <p>
                      The thing that makes patio work in Clifton a little different from some other areas is the soil. Boonton series clay holds onto moisture, and when that moisture freezes in the winter it expands and pushes whatever is sitting on top of it out of alignment. A patio that was laid on a thin base or without proper drainage can start shifting and settling within a few years because the ground underneath is doing the moving for it. That is why we dig deeper than a lot of contractors do and compact the aggregate base in lifts, because on clay soil you do not get a second chance to get the foundation right.
                    </p>
                    <p>
                      We also get a fair number of calls from homeowners who searched for stamped concrete and ended up finding us. We will be upfront about it: stamped concrete does not hold up well in North Jersey. The freeze-thaw cycling cracks the surface, and once that seal breaks, the deterioration speeds up fast. Pavers handle the same conditions better because each unit can flex independently, and if one does crack you can pull it and replace just that piece without redoing the whole patio.{" "}
                      <Link to="/paving-services/paver-patios" className="text-secondary hover:underline">Learn more about our paver patio installation process</Link>.
                    </p>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="rounded-xl overflow-hidden border border-border bg-black/40">
                      <img
                        src={patioImage}
                        alt="New patio paver installation on a residential property in Clifton, Passaic County, NJ, Masonry Pro construction."
                        className="w-full h-auto object-contain"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Retaining Walls — Image LEFT, Content RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  Retaining Walls for Sloped Lots Near <span className="text-gradient-gold">Garrett Mountain and Western Clifton</span>
                </h2>
                <div className="grid lg:grid-cols-5 gap-12 items-center">
                  <div className="lg:col-span-2">
                    <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                      <img
                        src={retainingWallImage}
                        alt="Paver patio with a fire pit and retaining wall, installed by Masonry Pro Construction at Clifton, Passaic County, New Jersey"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div className="lg:col-span-3 space-y-6 text-lg text-muted-foreground">
                    <p>
                      The western side of Clifton, where the city borders Garrett Mountain and the First Watchung Ridge, has some of the steepest residential lots you will find in Passaic County. Neighborhoods like Albion and Montclair Heights sit on terrain that rises sharply from the streets, and that grade change means a lot of properties either already have retaining walls or need them.
                    </p>
                    <p>
                      A retaining wall on a sloped lot is not just a landscaping feature. It is a structural element that holds back soil, manages water runoff, and protects whatever is below it (your driveway, your foundation, your neighbor's yard). When the soil behind that wall is clay-heavy, as it is across most of Clifton, lateral pressure increases every time the ground absorbs water and then freezes, which is why drainage behind the wall is just as important as the wall itself. We install filter fabric, gravel backfill, and weep holes on every{" "}
                      <Link to="/masonry-services/retaining-walls" className="text-secondary hover:underline">retaining wall</Link> job because skipping that step is how walls end up leaning or cracking within a few years.
                    </p>
                    <p>
                      Permit-wise, Clifton requires a building permit for retaining walls, and the application needs to include a certified plot plan showing the exact location, elevation, and design. If the wall is over 4 feet tall, the{" "}
                      <a href="https://diyretainingwall.com/nj-requirements/" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">NJ Uniform Construction Code</a><sup className="text-secondary">3</sup> requires engineering calculations from a licensed professional engineer. We handle the permit coordination with the Clifton Building Department so you do not have to figure that out on your own. Need a{" "}
                      <Link to="/masonry-services/retaining-wall-repair" className="text-secondary hover:underline">retaining wall repair</Link>? We do that too.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Mid-page CTA */}
          <InlineCTA heading="Need a Wall or Patio Estimate?" highlightedHeading="We're 10 Minutes Away" description="Call or text us directly or scroll down to request your free estimate." />

          {/* Section 6: Steps & Stoops — Content LEFT, Image RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  Masonry Steps and Stoops on <span className="text-gradient-gold">Clifton's Older Homes</span>
                </h2>
                <div className="grid lg:grid-cols-5 gap-12 items-center">
                  <div className="lg:col-span-3 space-y-6 text-lg text-muted-foreground">
                    <p>
                      Front steps and stoops are probably the single most common repair call we get from Clifton homeowners, and it makes sense when you think about how old a lot of these homes are. The Cape Cods and Colonials built in the late 1940s and 1950s, the split-levels from the 1960s, the ranches scattered across Richfield and Athenia, most of them still have the original poured concrete steps that were put in when the house was built. After 50 to 70 years of salt, rain, snow, and freeze-thaw, that concrete starts to crack and crumble.
                    </p>
                    <p>
                      What we see on a lot of these older Clifton homes is that the original steps were poured with shallow footings and little to no reinforcement. That was common for the era and nobody thought twice about it at the time, but it means the stoop can start to settle unevenly or pull away from the house as the ground underneath moves. On split-levels in particular, the raised front entry means a taller stoop with more steps, which puts more weight on those footings and makes proper depth even more important.
                    </p>
                    <p>
                      A stoop that is pulling away from the house is more than a cosmetic issue. That gap between the stoop and the foundation is a place where water gets in, and water getting behind your foundation wall is the kind of problem that costs a lot more to fix than replacing the steps would have. If your front steps are cracked, leaning, or separating from the house, it is worth getting someone out to look at it sooner rather than later.{" "}
                      <Link to="/masonry-services/steps-stoops" className="text-secondary hover:underline">Learn more about our masonry steps and stoops work</Link>. There is also some history behind <Link to="/pro-tips/what-is-a-stoop" className="text-secondary hover:underline">why so many North Jersey homes have a stoop</Link>.
                    </p>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="aspect-[3/4] rounded-xl overflow-hidden border border-border">
                      <img
                        src={stepsImage}
                        alt="New stone steps and stoop installation leading to a black door in Passaic, Passaic County, NJ. Masonry Pro Construction"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 7: Concrete Driveways — Image LEFT, Content RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  Concrete Driveways That Hold Up on <span className="text-gradient-gold">Clifton's Residential Streets</span>
                </h2>
                <div className="grid lg:grid-cols-5 gap-12 items-center">
                  <div className="lg:col-span-2">
                    <div className="aspect-[3/2] rounded-xl overflow-hidden border border-border">
                      <img
                        src={concreteDrivewayImage}
                        alt="New concrete driveway leading to a two-car garage at a residential property in Clifton, Passaic County, NJ. Masonry Pro Construction"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div className="lg:col-span-3 space-y-6 text-lg text-muted-foreground">
                    <p>
                      A lot of the driveways in Clifton are original to the home, which means they have been sitting through 50 or 60 winters of salt, plowing, and freeze-thaw without ever being replaced. Surface spalling (where the top layer flakes and pits) is the first thing most homeowners notice, but by the time you can see that, the concrete underneath has usually been cracking for a while because water has been getting into the slab and expanding every time it freezes.
                    </p>
                    <p>
                      Clifton's residential lots are on the narrower side in a lot of neighborhoods, so driveway replacement needs to account for property line setbacks and sometimes shared access points with neighbors. About 40% of properties in Clifton are multi-family, which can mean wider driveway pads or configurations that have to work for more than one household. We measure the lot and check the setbacks before we quote the job so there are no surprises once work starts.
                    </p>
                    <p>
                      One thing to know about driveway work in Clifton: if your project involves modifying the curb opening or driveway apron, you need a driveway cut and curb permit from the Clifton Engineering Department. The fee is $25, and there are specific regulations on driveway width and location{" "}
                      <a href="https://www.cliftonnj.org/233/Permits" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">that the city enforces</a><sup className="text-secondary">4</sup>. For work on streets maintained by Passaic County, you may also need a separate county road permit. We sort out which permits apply and handle the applications as part of the project.{" "}
                      <Link to="/concrete-services/driveways" className="text-secondary hover:underline">Learn more about our concrete driveway installations</Link>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 8: Services List */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Services We Provide in <span className="text-gradient-gold">Clifton</span>
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Here is a quick look at the work we do across Clifton's neighborhoods. Each service links to a page with more detail on materials, process, and what to expect.
                </p>
                <ul className="space-y-4 text-lg text-muted-foreground">
                  <li>
                    <Link to="/paving-services/paver-patios" className="text-secondary hover:underline font-bold">Paver Patio Installation:</Link>{" "}
                    Custom patios with proper base prep for clay soil. Brick, bluestone, natural stone, and concrete pavers.
                  </li>
                  <li>
                    <Link to="/masonry-services/retaining-walls" className="text-secondary hover:underline font-bold">Retaining Wall Installation:</Link>{" "}
                    Structural and decorative walls for sloped lots, including drainage and engineering coordination.
                  </li>
                  <li>
                    <Link to="/masonry-services/retaining-wall-repair" className="text-secondary hover:underline font-bold">Retaining Wall Repair:</Link>{" "}
                    Fixing leaning, cracked, or bulging walls and addressing the drainage problems behind them.
                  </li>
                  <li>
                    <Link to="/masonry-services/steps-stoops" className="text-secondary hover:underline font-bold">Masonry Steps &amp; Stoops:</Link>{" "}
                    New construction and replacement steps for older homes, built on proper footings.
                  </li>
                  <li>
                    <Link to="/concrete-services/driveways" className="text-secondary hover:underline font-bold">Concrete Driveway Installation:</Link>{" "}
                    Full driveway replacement with reinforcement and drainage built in.
                  </li>
                  <li>
                    <Link to="/masonry-services/stone-veneer" className="text-secondary hover:underline font-bold">Stone Veneer Installation:</Link>{" "}
                    Facade upgrades for Colonials, split-levels, and foundation wraps.
                  </li>
                  <li>
                    <Link to="/masonry-services/brick-work" className="text-secondary hover:underline font-bold">Brick &amp; Block Work:</Link>{" "}
                    Repairs, repointing, and new construction for residential and commercial properties.
                  </li>
                  <li>
                    <Link to="/waterproofing-services/basement-waterproofing" className="text-secondary hover:underline font-bold">Basement Waterproofing:</Link>{" "}
                    Interior and exterior waterproofing for Clifton basements affected by clay soil drainage.
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 9: FAQ Cards */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked Questions About Masonry Work in{" "}
                <span className="text-gradient-gold">Clifton</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background rounded-xl p-6 border border-border"
                  >
                    <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {faq.link ? (
                        <>
                          {faq.answer.split(faq.link.text)[0]}
                          <a
                            href={faq.link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-secondary hover:underline"
                          >
                            {faq.link.text}
                          </a>
                          {faq.answer.split(faq.link.text)[1]}
                        </>
                      ) : (
                        faq.answer
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Section 10: Citations Accordion */}
          <section className="py-12">
            <div className="container mx-auto px-4 max-w-4xl">
              <Accordion type="single" collapsible>
                <AccordionItem value="citations" className="border-border">
                  <AccordionTrigger className="text-sm text-muted-foreground hover:no-underline">
                    Sources &amp; Citations
                  </AccordionTrigger>
                  <AccordionContent>
                    <ol className="list-decimal list-inside space-y-3 text-sm text-muted-foreground">
                      <li>
                        Clifton, NJ – Hamilton House Museum. City of Clifton official website. cliftonnj.org/256/Hamilton-House-Museum
                      </li>
                      <li>
                        Boonton series soil (clay-heavy glacial till in Passaic County). USDA NRCS Web Soil Survey: websoilsurvey.nrcs.usda.gov. Shore Systems Group NJ Soil Guide: shorellc.com/articles/nj-soils-and-testing-guide
                      </li>
                      <li>
                        Retaining wall permit threshold (4 ft engineering requirement). NJ Uniform Construction Code / IRC Section R404.4: diyretainingwall.com/nj-requirements. Clifton local code Chapter 461 (eCode360): ecode360.com/8522866. Clifton Building Department: cliftonnj.org/227/Building
                      </li>
                      <li>
                        Clifton driveway/curb permit ($25 fee, width/location regulations). City of Clifton Engineering Department: cliftonnj.org/233/Permits. Clifton construction code fee schedule (eCode360): ecode360.com/8515259
                      </li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </section>

          {/* Section 11: Final CTA with Contact Form */}
          <CTASection
            serviceName="Clifton Masonry Project"
            heading="Get a Free Estimate on Your"
            highlightedHeading="Clifton Masonry Project"
          />
          <section className="py-12 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center space-y-2">
                <p className="text-xl font-bold">Masonry Pro Construction</p>
                <p className="text-muted-foreground">36 Highview Ave, Totowa, NJ 07512</p>
                <a href="tel:9737252395" className="text-secondary hover:underline text-lg font-semibold block">
                  (973) 725-2395
                </a>
                <p className="text-sm text-muted-foreground italic mt-4">
                  NJ HIC License{" "}
                  <a href="https://newjersey.mylicense.com/verification/Search.aspx?facility=Y" target="_blank" rel="noopener noreferrer" className="hover:text-secondary transition-colors underline">#13VH06777200</a>
                </p>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
